import type {
  DesignConnectorShape,
  DesignFill,
  DesignShape,
  DesignShapeType,
  DesignStroke,
} from '../../types/design'

export const dasharray = (stroke: DesignStroke | null): string | undefined => {
  if (!stroke) return undefined
  switch (stroke.style) {
    case 'DASHED': return `${stroke.width * 4} ${stroke.width * 3}`
    case 'DOTTED': return `${stroke.width} ${stroke.width * 2}`
    default: return undefined
  }
}

export const blendModeCss = (mode: string): string => mode.toLowerCase().replaceAll('_', '-')

export const fillToPaint = (fill: DesignFill, gradientId: string): string => {
  if (fill.kind === 'NONE') return 'none'
  if (fill.kind === 'SOLID') return fill.color
  return `url(#${gradientId})`
}

export type PathBox = { x: number; y: number; width: number; height: number }

const polyPoints = (points: Array<[number, number]>) =>
  points.map(([x, y]) => `${x},${y}`).join(' ')

export const shapePathPoints = (type: DesignShapeType, box: PathBox): string => {
  const { x, y, width: w, height: h } = box
  switch (type) {
    case 'TRIANGLE':
      return polyPoints([
        [x + w / 2, y],
        [x + w, y + h],
        [x, y + h],
      ])
    case 'DIAMOND':
      return polyPoints([
        [x + w / 2, y],
        [x + w, y + h / 2],
        [x + w / 2, y + h],
        [x, y + h / 2],
      ])
    case 'PARALLELOGRAM': {
      const skew = w * 0.18
      return polyPoints([
        [x + skew, y],
        [x + w, y],
        [x + w - skew, y + h],
        [x, y + h],
      ])
    }
    case 'HEXAGON': {
      const inset = w * 0.18
      return polyPoints([
        [x + inset, y],
        [x + w - inset, y],
        [x + w, y + h / 2],
        [x + w - inset, y + h],
        [x + inset, y + h],
        [x, y + h / 2],
      ])
    }
    default:
      return ''
  }
}

export const shapePathD = (type: DesignShapeType, box: PathBox, opts?: { cornerRadius?: number; sides?: number; innerRatio?: number }): string => {
  const { x, y, width: w, height: h } = box
  switch (type) {
    case 'CYLINDER': {
      const ry = Math.min(h * 0.12, 16)
      return [
        `M${x},${y + ry}`,
        `Q${x},${y} ${x + w / 2},${y}`,
        `Q${x + w},${y} ${x + w},${y + ry}`,
        `L${x + w},${y + h - ry}`,
        `Q${x + w},${y + h} ${x + w / 2},${y + h}`,
        `Q${x},${y + h} ${x},${y + h - ry}`,
        `Z`,
      ].join(' ')
    }
    case 'DOCUMENT': {
      const wave = h * 0.16
      return [
        `M${x},${y}`,
        `L${x + w},${y}`,
        `L${x + w},${y + h - wave}`,
        `Q${x + (3 * w) / 4},${y + h + wave / 2} ${x + w / 2},${y + h - wave / 2}`,
        `Q${x + w / 4},${y + h - wave * 1.5} ${x},${y + h - wave / 2}`,
        `Z`,
      ].join(' ')
    }
    case 'TERMINATOR': {
      const r = h / 2
      return [
        `M${x + r},${y}`,
        `L${x + w - r},${y}`,
        `A${r},${r} 0 0 1 ${x + w - r},${y + h}`,
        `L${x + r},${y + h}`,
        `A${r},${r} 0 0 1 ${x + r},${y}`,
        `Z`,
      ].join(' ')
    }
    case 'OFF_PAGE': {
      const notch = h * 0.4
      return [
        `M${x},${y}`,
        `L${x + w},${y}`,
        `L${x + w},${y + h - notch}`,
        `L${x + w / 2},${y + h}`,
        `L${x},${y + h - notch}`,
        `Z`,
      ].join(' ')
    }
    case 'POLYGON': {
      const sides = Math.max(3, opts?.sides ?? 6)
      const cx = x + w / 2, cy = y + h / 2
      const rx = w / 2, ry = h / 2
      const pts: string[] = []
      for (let i = 0; i < sides; i++) {
        const a = (Math.PI * 2 * i) / sides - Math.PI / 2
        pts.push(`${cx + rx * Math.cos(a)},${cy + ry * Math.sin(a)}`)
      }
      return `M${pts.join(' L')} Z`
    }
    case 'STAR': {
      const sides = Math.max(3, opts?.sides ?? 5)
      const inner = (opts?.innerRatio ?? 0.5)
      const cx = x + w / 2, cy = y + h / 2
      const rx = w / 2, ry = h / 2
      const pts: string[] = []
      for (let i = 0; i < sides * 2; i++) {
        const a = (Math.PI * i) / sides - Math.PI / 2
        const r = i % 2 === 0 ? 1 : inner
        pts.push(`${cx + rx * r * Math.cos(a)},${cy + ry * r * Math.sin(a)}`)
      }
      return `M${pts.join(' L')} Z`
    }
    default:
      return ''
  }
}

export const portsForShape = (box: PathBox) => ({
  N: { x: box.x + box.width / 2, y: box.y },
  S: { x: box.x + box.width / 2, y: box.y + box.height },
  W: { x: box.x, y: box.y + box.height / 2 },
  E: { x: box.x + box.width, y: box.y + box.height / 2 },
})

export const resolveConnectorEndpoint = (
  endpoint: DesignConnectorShape['from'],
  shapesById: Map<string, DesignShape>,
): { x: number; y: number } | null => {
  if (endpoint.kind === 'FREE') return { x: endpoint.x, y: endpoint.y }
  const shape = shapesById.get(endpoint.shapeId)
  if (!shape) return null
  const box = { x: shape.position.x, y: shape.position.y, width: shape.size.width, height: shape.size.height }
  if (!endpoint.port || endpoint.port === 'AUTO') {
    return { x: box.x + box.width / 2, y: box.y + box.height / 2 }
  }
  const ports = portsForShape(box)
  return ports[endpoint.port]
}

export const buildConnectorPath = (
  shape: DesignConnectorShape,
  shapesById: Map<string, DesignShape>,
): { d: string; midpoint: { x: number; y: number } } | null => {
  const from = resolveConnectorEndpoint(shape.from, shapesById)
  const to = resolveConnectorEndpoint(shape.to, shapesById)
  if (!from || !to) return null
  switch (shape.routing) {
    case 'STRAIGHT':
      return {
        d: `M${from.x},${from.y} L${to.x},${to.y}`,
        midpoint: { x: (from.x + to.x) / 2, y: (from.y + to.y) / 2 },
      }
    case 'CURVED': {
      const dx = (to.x - from.x) * 0.45
      const c1 = { x: from.x + dx, y: from.y }
      const c2 = { x: to.x - dx, y: to.y }
      return {
        d: `M${from.x},${from.y} C${c1.x},${c1.y} ${c2.x},${c2.y} ${to.x},${to.y}`,
        midpoint: { x: (from.x + to.x) / 2, y: (from.y + to.y) / 2 },
      }
    }
    case 'ORTHOGONAL':
    default: {
      const mx = (from.x + to.x) / 2
      return {
        d: `M${from.x},${from.y} L${mx},${from.y} L${mx},${to.y} L${to.x},${to.y}`,
        midpoint: { x: mx, y: (from.y + to.y) / 2 },
      }
    }
  }
}

export const arrowMarkerId = (
  variant: string,
  arrow: 'NONE' | 'TRIANGLE' | 'OPEN' | 'DIAMOND' | 'CIRCLE',
  color: string,
): string | undefined => {
  if (arrow === 'NONE') return undefined
  return `arrow-${variant}-${arrow}-${color.replace('#', '')}`
}
